import { DatosSegundoPaso } from "application/store/models/segundoPaso";
import { getIsAddressStoredSelector } from "application/store/reducers/QuintoPasoReducer";
import { setShowErrorRedux } from "application/store/reducers/ShowErrorReducer";
import { setLoadingState } from "application/store/reducers/GlobalLoadingStateReducer";
import { setImageState } from "application/store/reducers/uploadDocumentsReducer";
import store from "application/store/Store";
import { GO_TO } from "infrastructure/api";
import {
  DataOcrResponseStatus,
  getDataFromOCR,
  postAddressAfterInvoiceProcessed,
  ProcessType,
} from "infrastructure/services";
import { postSubmission } from "infrastructure/services/submissionService";
import { MixpanelService } from "infrastructure/services/tracker/mixpanel.service";
import { TrackerService } from "infrastructure/services/tracker/tracker.service";
import { useNavigate } from "react-router-dom";
import { Checkpoints } from "./CheckpointEvents";
import { setSeventhStep } from "application/store/reducers/SeptimoPasoReducer";
import {
  DNIC_FIELDS_INITIAL_STATE,
  setThirdStep,
  TercerPasoState,
} from "application/store/reducers/TercerPasoReducer";
import { setSecondStep } from "application/store/reducers/SegundoPasoReducer";
import { APP_ENVIRONMENT } from "application/utils/constants";
import { ProductTypeQueryParam } from "application/store/models/seleccionarProductoPaso";
import { GA_EVENTS } from "infrastructure/services/tracker/ga.types";
import { getIsEstablishmentAddressStoredSelector } from "application/store/reducers/ActivityReducer";
import { getApplyBiometric } from "application/store/reducers/ApplyBiometricReducer";
import { SucursalesNavigationState } from "infrastructure/api/navigation-state";

type CheckpointHandlerParams = {
  checkpoint: Checkpoints;
  navigate: ReturnType<typeof useNavigate>;
  CALLING_DNIC?: boolean;
};

const getStateAndDispatch = () => {
  return {
    state: store.getState(),
    dispatch: store.dispatch,
  };
};

const isEveryZeroes = (date: string): string =>
  date.split("").every((v) => v === "0") ? "" : date;

const goToSucursales = (
  navigate: ReturnType<typeof useNavigate>,
  state?: SucursalesNavigationState,
) => {
  navigate(
    { pathname: GO_TO.SUCURSALES, search: `?finished=${true}` },
    state ? { state, replace: true } : { replace: true },
  );
};

export const onCedulaProcessingDone = async ({
  navigate,
  CALLING_DNIC = false,
}: CheckpointHandlerParams) => {
  const { state, dispatch } = getStateAndDispatch();

  const isCollege = state.SeleccionarProductoState.isCollegePack;
  const isLegacyUser = state.collegeUserReducer.userType === 1;
  const isPymes = state.SeleccionarProductoState.isPymes;
  const documentNumber = state.onboardingState.documentNumber;

  const { isSuccess, data, status } = await getDataFromOCR(
    ProcessType.FOR_CEDULA,
    documentNumber,
  );

  if (isSuccess) {
    try {
      const nameArray = data?.name?.split(" ");
      const lastNameArray = data?.lastName?.split(" ");

      const restName = nameArray.slice(1).join(" ");
      const restLastNameArray = lastNameArray.slice(1).join(" ");

      let isComingFromDNIC = DNIC_FIELDS_INITIAL_STATE;

      const birthDate = data?.birthDate?.split("/");

      const nationality = data?.nationality;

      // Se rechazan CI extranjeras hasta que se suba el OCR del backend que los rechaza directamente desde el endpoint
      // Hasta que eso pase, se deja esta validación a nivel del frontend
      if (!["dev", "prep"].includes(APP_ENVIRONMENT)) {
        if (nationality !== "URUGUAYA" && !isPymes) {
          MixpanelService.track("Errored", {
            When: "ID Processing",
            Why: "Foreginer",
          });
          return goToSucursales(navigate, { errorCode: "ONB012" });
        }
      }

      if (CALLING_DNIC) {
        const lastName = data?.lastName?.trim();
        const words = lastName.split(/\s+/);

        if (words.length === 1) {
          return goToSucursales(navigate, { errorCode: "ONB015" });
        }
      }

      if (CALLING_DNIC) {
        isComingFromDNIC = {
          theName: !!nameArray[0],
          theSecondName: !!restName,
          theLastName: !!lastNameArray[0],
          theSecondLastName: !!restLastNameArray,
          theBirthdayDay: !!isEveryZeroes(birthDate[0]),
          theBirthdayMonth: !!isEveryZeroes(birthDate[1]),
          theBirthdayYear: !!isEveryZeroes(birthDate[2]),
          theSex: !!data?.sex,
        };
      }
      const datosTercerPaso: TercerPasoState = {
        prospectId: store.getState().TercerPasoState.prospectId,
        name: nameArray[0] || "",
        secondName: restName || "",
        lastName: lastNameArray[0] || "",
        secondLastName: restLastNameArray || "",
        birthdayDay: isEveryZeroes(birthDate[0]),
        birthdayMonth: isEveryZeroes(birthDate[1]),
        birthdayYear: isEveryZeroes(birthDate[2]),
        birthdayCountryCode: "",
        residencyCountryCode: "",
        nationality: nationality,
        sex: data?.sex,
        phone: "",
        email: "",
        dniDueDate: {
          dueDay: "",
          dueMonth: "",
          dueYear: "",
        },
        isComingFromDNIC,
      };
      dispatch(setThirdStep(datosTercerPaso));
      if (isCollege && isLegacyUser) {
        navigate(GO_TO.SEXTO_PASO);
      } else {
        navigate(GO_TO.TERCER_PASO);
      }
      TrackerService.trackGA(GA_EVENTS.FLOW.IDENTIFICATION_VERIFICATION);
      MixpanelService.track("Completed ID Verification");
      if (!CALLING_DNIC) {
        dispatch(setLoadingState(false));
      }
    } catch (e) {
      MixpanelService.track("Errored", {
        When: "ID Procedding",
      });
      console.error(e);
    }
  } else {
    switch (status) {
      case DataOcrResponseStatus.DATA_NO_ES_VALIDA:
        dispatch(setShowErrorRedux(true));
        // reset segundo paso
        const datos: DatosSegundoPaso = {
          documentBackImage: "",
          documentFrontImage: "",
        };
        dispatch(setSecondStep(datos));
        MixpanelService.track("Errored", {
          When: "ID Processing",
          Why: "Invalid Data",
        });
        break;
      case DataOcrResponseStatus.ONBOARDING_NO_FUE_ENVIADO:
      case DataOcrResponseStatus.IMAGEN_NO_FUE_CARGADA:
      case DataOcrResponseStatus.OBSERVACIONES:
      case DataOcrResponseStatus.EXPIRO:
        MixpanelService.track("Errored", {
          When: "ID Processing",
          Why: "OCR rejected it",
        });
        goToSucursales(navigate);
        break;
      case DataOcrResponseStatus.MENOR_DE_EDAD:
        goToSucursales(navigate, { isMinor: true });
        MixpanelService.track("Errored", {
          When: "ID Processing",
          Why: "Not adult",
        });
        break;
      case DataOcrResponseStatus.REQUEST_MAL_FORMADO:
      case DataOcrResponseStatus.ERROR_INESPERADO:
      case DataOcrResponseStatus.ERROR_PROCESANDO_DOCUMENTO:
        navigate(GO_TO.REDIRECT_ERROR, { replace: true });
        break;
      default:
        goToSucursales(navigate);
        break;
    }
    dispatch(setLoadingState(false));
  }
};

const onInvoiceProcessingDone = async ({
  navigate,
}: CheckpointHandlerParams) => {
  const { state, dispatch } = getStateAndDispatch();
  const paso7 = state.SeptimoPasoState;
  const isPymes = state.SeleccionarProductoState.isPymes;

  const { isSuccess: isValidDomicilio, status } =
    await postAddressAfterInvoiceProcessed({
      city: paso7.ciudad,
      department: paso7.departamento,
      streetName: paso7.calle,
      streetIdentification: paso7.numero,
      complementaryData: paso7.datosComplementarios,
    });

  if (isValidDomicilio) {
    TrackerService.trackGA(GA_EVENTS.FLOW.ADDRESS_VERIFICATION);
    MixpanelService.track("Completed Address Verification");
    if (isPymes) {
      navigate(GO_TO.MULTIPLE_DOCUMENTS_UPLOAD);
    } else {
      const successFinal = await postSubmission(state);
      if (successFinal) {
        // Does nothing (but leaves this way for readability).
        // Redirection will be done in CheckpointR.
      } else {
        console.error("esta fallando el /submission");
        goToSucursales(navigate);
      }
    }

    dispatch(setLoadingState(false));
  } else {
    switch (status) {
      case DataOcrResponseStatus.DIRECCION_NO_ES_DOMICILIO:
        dispatch(setShowErrorRedux(true));
        // reset septimo paso
        dispatch(
          setSeventhStep({
            constanciaAMiNombre: "",
            vivoEnDomicilio: false,
          }),
        );
        break;
      case DataOcrResponseStatus.ONBOARDING_NO_FUE_ENVIADO:
      case DataOcrResponseStatus.IMAGEN_NO_FUE_CARGADA:
        goToSucursales(navigate);
        break;
      case DataOcrResponseStatus.REQUEST_MAL_FORMADO:
      case DataOcrResponseStatus.ERROR_INESPERADO:
      case DataOcrResponseStatus.ERROR_PROCESANDO_DOCUMENTO:
        navigate(GO_TO.REDIRECT_ERROR, { replace: true });
        break;
      default:
        if (!window.isFinalState) {
          goToSucursales(navigate);
        }
        break;
    }
    dispatch(setLoadingState(false));
  }
};

const onSelfieProcessingDone = async ({
  navigate,
}: CheckpointHandlerParams) => {
  const { state, dispatch } = getStateAndDispatch();
  const { isPymes } = state.SeleccionarProductoState;
  const isAddressStored = getIsAddressStoredSelector(state);
  const isEstablishmentAddressStored =
    getIsEstablishmentAddressStoredSelector(state);
  const isCollege = state.SeleccionarProductoState.isCollegePack;
  const isCollegeLanding = state.onboardingState.urlParams?.includes(
    ProductTypeQueryParam.CollegePack,
  );
  const isLastPage =
    (isPymes && isEstablishmentAddressStored) || isAddressStored;

  if (isLastPage) {
    dispatch(setSeventhStep({}));
  }

  TrackerService.trackGA(GA_EVENTS.FLOW.LIFE_PROOF_SELFIE);
  MixpanelService.track("Completed Selfie Verification");
  if (isPymes && !getApplyBiometric) {
    dispatch(setLoadingState(false));
    // SE ELIMINA LA NAVEGACION A REFERENCIAS Y SE PASA DIRECTAMENTE AL PASO SIETE
    // navigate(GO_TO.REFERRALS);
    navigate(GO_TO.SEPTIMO_PASO);
  } else if (isCollege) {
    // if (isCollege) {
    dispatch(setLoadingState(false));
    if (isCollegeLanding) {
      navigate(GO_TO.ADD_DEBIT);
    } else {
      navigate(GO_TO.AUTOMATIC_DEBIT);
    }
  } else {
    if (!isLastPage) {
      navigate(GO_TO.SEPTIMO_PASO);
      dispatch(setLoadingState(false));
    } else {
      const successFinal = await postSubmission(state);
      if (successFinal) {
        // Does nothing (but leaves this way for readability).
        // Redirection will be done in CheckpointR.
      } else {
        console.error("esta fallando el /submission");
        dispatch(setLoadingState(false));
        goToSucursales(navigate);
      }
    }
  }
};

const onPymeDocumentProcessingDone = async ({
  navigate,
  checkpoint,
}: CheckpointHandlerParams) => {
  const { state, dispatch } = getStateAndDispatch();
  const documentStates = { ...state.UploadDocumentState };
  const fieldState: keyof typeof documentStates =
    checkpoint === Checkpoints.FILE_PROCESSING_RUT_DONE
      ? "RUT"
      : checkpoint === Checkpoints.FILE_PROCESSING_DGI_DONE
        ? "DGI"
        : "BPS";

  dispatch(setImageState({ field: fieldState, newState: "success" }));

  documentStates[fieldState] = "success";
  if (Object.values(documentStates).every((state) => state === "success")) {
    const successFinal = await postSubmission(state);
    if (successFinal) {
      // Does nothing (but leaves this way for readability).
      // Redirection will be done in CheckpointR.
    } else {
      console.error("esta fallando el /submission");
      goToSucursales(navigate);
    }
  }
};

const onPymeImageError = ({
  navigate,
  checkpoint,
}: CheckpointHandlerParams) => {
  const { state, dispatch } = getStateAndDispatch();
  const { UploadDocumentState: documentStates } = state;
  const fieldState: keyof typeof documentStates =
    checkpoint === Checkpoints.FILE_PROCESSING_RUT_DONE
      ? "RUT"
      : checkpoint === Checkpoints.FILE_PROCESSING_DGI_DONE
        ? "DGI"
        : "BPS";
  dispatch(setImageState({ field: fieldState, newState: "error" }));
  dispatch(setLoadingState(false));
};

const onSubmitOnboardingDone = async ({
  navigate,
}: CheckpointHandlerParams) => {
  const { dispatch } = getStateAndDispatch();
  dispatch(setLoadingState(false));
  MixpanelService.track("Onboarding Completed");
  navigate({ pathname: GO_TO.FINAL, search: `?finished=${true}` });
  window.localStorage.setItem("finished", "true");
};

export const CheckpointHandlers = {
  [Checkpoints.CEDULA_PROCESSING_DONE]: onCedulaProcessingDone,
  [Checkpoints.INVOICE_PROCESSING_DONE]: onInvoiceProcessingDone,
  [Checkpoints.SUBMIT_ONBOARDING_DONE]: onSubmitOnboardingDone,
  [Checkpoints.UPLOAD_SELFIE_IMAGE_DONE]: onSelfieProcessingDone,
  [Checkpoints.FILE_PROCESSING_RUT_DONE]: onPymeDocumentProcessingDone,
  [Checkpoints.FILE_PROCESSING_DGI_DONE]: onPymeDocumentProcessingDone,
  [Checkpoints.FILE_PROCESSING_BPS_DONE]: onPymeDocumentProcessingDone,
  [Checkpoints.FILE_PROCESSING_RUT_ERROR]: onPymeImageError,
  [Checkpoints.FILE_PROCESSING_DGI_ERROR]: onPymeImageError,
  [Checkpoints.FILE_PROCESSING_BPS_ERROR]: onPymeImageError,
} as const;

export const handleCheckpoint = async (params: CheckpointHandlerParams) => {
  if (CheckpointHandlers[params.checkpoint]) {
    CheckpointHandlers[params.checkpoint](params);
  }
};
